import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import Button from '../../components/common/Button';
import { useAuth } from '../../contexts/AuthContext';
import SEO from '../../components/SEO';

const PricingContainer = styled.div`
  padding: 3rem 0;
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
  
  @media (min-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
    max-width: 900px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const PricingCard = styled.div`
  background-color: white;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  padding: 2rem;
  display: flex;
  flex-direction: column;
  height: 100%;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  }
  
  ${props => props.popular && `
    border: 2px solid var(--primary);
    position: relative;
    
    &::before {
      content: 'Most Popular';
      position: absolute;
      top: -12px;
      left: 50%;
      transform: translateX(-50%);
      background-color: var(--primary);
      color: white;
      padding: 0.25rem 1rem;
      border-radius: 20px;
      font-size: 0.8rem;
      font-weight: 600;
    }
  `}
`;

const PlanName = styled.h3`
  color: var(--dark);
  margin-bottom: 0.5rem;
`;

const PlanPrice = styled.div`
  margin-bottom: 1.5rem;
  
  .price {
    font-size: 2.5rem;
    font-weight: 700;
    color: var(--primary);
  }
  
  .period {
    font-size: 1rem;
    color: var(--gray);
  }
`;

const PlanDescription = styled.p`
  color: var(--gray);
  margin-bottom: 1.5rem;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 2rem;
  flex-grow: 1;
  
  li {
    padding: 0.5rem 0;
    display: flex;
    align-items: center;
    
    &::before {
      content: '✓';
      color: var(--secondary);
      font-weight: bold;
      margin-right: 0.75rem;
    }
  }
`;

const ComparisonTable = styled.div`
  overflow-x: auto;
  margin-bottom: 3rem;
  
  table {
    width: 100%;
    border-collapse: collapse;
    
    th, td {
      padding: 1rem;
      text-align: left;
      border-bottom: 1px solid #f0f0f0;
    }
    
    th {
      background-color: #f8f9fa;
      font-weight: 600;
    }
    
    tr:last-child td {
      border-bottom: none;
    }
    
    .check {
      color: var(--secondary);
      font-size: 1.2rem;
    }
    
    .cross {
      color: var(--gray);
    }
  }
`;

const FaqSection = styled.div`
  margin-bottom: 3rem;
`;

const AuthenticatedMessage = styled.div`
  background-color: #f5f5f5;
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
  text-align: center;
  border-left: 4px solid var(--secondary);
  
  h3 {
    color: var(--dark);
    margin-bottom: 1rem;
  }
  
  p {
    margin-bottom: 1.5rem;
    color: var(--gray);
  }
`;

const PricingPage = () => {
  const { isAuthenticated, currentUser } = useAuth();
  
  // In a real app, you would fetch the user's plan from your backend
  // For now, we'll assume the user has a basic plan
  const userPlan = "basic"; // This would come from your backend
  
  // SEO metadata based on the SEO strategy document
  const seoData = {
    title: "Affordable Baby Sleep Consultant Plans | Dozzy Pricing",
    description: "Get personalized baby sleep plans at a fraction of traditional sleep consultant costs. Plans starting at $49 for one child, $79 for two children.",
    keywords: [
      "affordable sleep consultant", 
      "sleep training cost", 
      "baby sleep plan pricing",
      "AI sleep consultant cost",
      "personalized sleep plan price",
      "sleep training app subscription",
      "baby sleep consultant fees"
    ],
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": "Dozzy Sleep Plans",
      "description": "AI-powered personalized sleep plans for babies and toddlers",
      "offers": [
        {
          "@type": "Offer",
          "name": "Basic Plan",
          "price": "49.00",
          "priceCurrency": "AUD",
          "description": "AI-generated personalized sleep plan for one child"
        },
        {
          "@type": "Offer",
          "name": "Family Plan",
          "price": "79.00",
          "priceCurrency": "AUD",
          "description": "AI-generated personalized sleep plans for up to 2 children"
        }
      ]
    }
  };
  
  return (
    <>
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        structuredData={seoData.structuredData}
        canonicalUrl="https://dozzy.au/pricing"
        ogType="website"
      />
      
      {/* Page Header */}
      <section className="py-5 bg-primary text-white text-center">
        <div className="container py-2">
          <h1>Simple, Transparent Pricing</h1>
          <p className="mx-auto" style={{ maxWidth: '700px' }}>
            Affordable sleep solutions tailored to your family's needs
          </p>
        </div>
      </section>
      
      <div className="container">
        <PricingContainer>
          {/* Message for authenticated users */}
          {isAuthenticated && (
            <AuthenticatedMessage>
              <h3>Welcome Back!</h3>
              <p>
                You already have the {userPlan === "basic" ? "Basic" : "Family"} Plan.
                {userPlan === "basic" ? 
                  " If you'd like to upgrade to the Family Plan, please contact us at support@dozzy.au." : 
                  " Thank you for choosing our Family Plan!"}
              </p>
              <Button to="/dashboard" variant="primary">Go to Dashboard</Button>
            </AuthenticatedMessage>
          )}
          
          {/* ONLY TWO PRICING CARDS */}
          <div className="text-center mb-5">
            <p className="mb-4">The only difference between our plans is the number of children you can create sleep plans for. All plans include the same comprehensive features.</p>
            <div className="alert alert-success d-inline-block mx-auto px-4 py-2">
              <i className="bi bi-check-circle-fill me-2"></i>
              <strong>One-time payment only</strong> — No subscriptions or recurring fees
            </div>
          </div>
          
          <PricingGrid>
            <PricingCard>
              <PlanName>Basic Plan</PlanName>
              <PlanPrice>
                <span className="price">$49</span>
                <span className="period"> one-time</span>
              </PlanPrice>
              <PlanDescription>
                Perfect for parents looking for AI-powered guidance with one child's sleep challenges.
              </PlanDescription>
              <FeatureList>
                <li>AI-generated personalized sleep plan for one child</li>
                <li>Based on your child's specific needs</li>
                <li>Detailed daily schedule</li>
                <li>Bedtime routine recommendations</li>
                <li>Night wakings guidance</li>
              </FeatureList>
              {isAuthenticated ? (
                userPlan === "basic" ? (
                  <Button variant="disabled" fullWidth disabled>Current Plan</Button>
                ) : (
                  <Button variant="disabled" fullWidth disabled>Already on Family Plan</Button>
                )
              ) : (
                <Button to="/signup?plan=basic" variant="outline" fullWidth>Get Started</Button>
              )}
            </PricingCard>
            
            <PricingCard popular>
              <PlanName>Family Plan</PlanName>
              <PlanPrice>
                <span className="price">$79</span>
                <span className="period"> one-time</span>
              </PlanPrice>
              <PlanDescription>
                Ideal for families with multiple children needing AI-powered sleep support.
              </PlanDescription>
              <FeatureList>
                <li>AI-generated personalized sleep plans for up to 2 children</li>
                <li>Based on each child's specific needs</li>
                <li>Detailed daily schedules</li>
                <li>Bedtime routine recommendations</li>
                <li>Night wakings guidance</li>
              </FeatureList>
              {isAuthenticated ? (
                userPlan === "family" ? (
                  <Button variant="disabled" fullWidth disabled>Current Plan</Button>
                ) : (
                  <Button as="a" href="mailto:support@dozzy.au" variant="primary" fullWidth>
                    Contact to Upgrade
                  </Button>
                )
              ) : (
                <Button to="/signup?plan=family" variant="primary" fullWidth>Get Started</Button>
              )}
            </PricingCard>
          </PricingGrid>
          
          {/* Gift a Sleep Plan Section */}
          <div className="text-center mb-5 mt-5">
            <h2 className="mb-3">Looking for a Thoughtful Gift?</h2>
            <p className="mb-4">Give the gift of sleep to new parents or anyone who could use better rest.</p>
            <Link to="/gift" className="btn btn-red btn-lg">
              <i className="bi bi-gift me-2"></i> Gift a Sleep Plan
            </Link>
          </div>
          
          <FaqSection>
            <h2 className="mb-4">Frequently Asked Questions</h2>
            <div className="accordion" id="pricingFaqAccordion">
              <div className="accordion-item">
                <h2 className="accordion-header" id="headingOne">
                  <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseOne" aria-expanded="false" aria-controls="collapseOne">
                    Do you offer refunds?
                  </button>
                </h2>
                <div id="collapseOne" className="accordion-collapse collapse" aria-labelledby="headingOne" data-bs-parent="#pricingFaqAccordion">
                  <div className="accordion-body">
                    <p>Yes, we offer a 7-day money-back guarantee. If you're not satisfied with your sleep plan, you can request a refund within 7 days of purchase.</p>
                    <p>However, due to the costs associated with generating personalized sleep programs, refunds are limited to 70% of the purchase price. The 30% non-refundable portion covers the costs of AI processing, data analysis, and program generation that have already been incurred.</p>
                  </div>
                </div>
              </div>
              
              <div className="accordion-item">
                <h2 className="accordion-header" id="headingTwo">
                  <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseTwo" aria-expanded="false" aria-controls="collapseTwo">
                    What plans does Dozzy offer?
                  </button>
                </h2>
                <div id="collapseTwo" className="accordion-collapse collapse" aria-labelledby="headingTwo" data-bs-parent="#pricingFaqAccordion">
                  <div className="accordion-body">
                    <p>Dozzy offers two simple plan options:</p>
                    <ul>
                      <li><strong>Basic Plan ($49):</strong> For families with one child, providing a personalized AI-generated sleep plan.</li>
                      <li><strong>Family Plan ($79):</strong> For families with up to two children, providing personalized AI-generated sleep plans for each child.</li>
                    </ul>
                    <p>Both plans include the same comprehensive features. The only difference is the number of children you can create sleep plans for.</p>
                  </div>
                </div>
              </div>
              
              <div className="accordion-item">
                <h2 className="accordion-header" id="headingThree">
                  <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseThree" aria-expanded="false" aria-controls="collapseThree">
                    Can I upgrade my plan later?
                  </button>
                </h2>
                <div id="collapseThree" className="accordion-collapse collapse" aria-labelledby="headingThree" data-bs-parent="#pricingFaqAccordion">
                  <div className="accordion-body">
                    <p>Yes, you can upgrade from a Basic Plan to a Family Plan at any time. You'll only pay the difference between your current plan and the new plan.</p>
                    <p>To upgrade, please contact our support team at support@dozzy.au to assist you with the upgrade process.</p>
                  </div>
                </div>
              </div>
              
              <div className="accordion-item">
                <h2 className="accordion-header" id="headingFour">
                  <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseFour" aria-expanded="false" aria-controls="collapseFour">
                    Is this a subscription or a one-time payment?
                  </button>
                </h2>
                <div id="collapseFour" className="accordion-collapse collapse" aria-labelledby="headingFour" data-bs-parent="#pricingFaqAccordion">
                  <div className="accordion-body">
                    <p>All our plans are <strong>one-time payments</strong>, not subscriptions. You pay once and get lifetime access to your personalized sleep plan(s). There are no recurring charges or hidden fees.</p>
                    <p>This includes the ability to make adjustments to your plan as your child grows and their sleep needs change.</p>
                  </div>
                </div>
              </div>
            </div>
          </FaqSection>
          
          <div className="text-center">
            <h2 className="mb-3">Ready to help your family get better sleep with AI?</h2>
            <p className="mb-4">Choose the plan that's right for you and start your journey to better sleep today.</p>
            {isAuthenticated ? (
              <Button to="/dashboard" variant="primary" size="large">Go to Dashboard</Button>
            ) : (
              <Button to="/signup?plan=family" variant="primary" size="large">Get Started Now</Button>
            )}
          </div>
        </PricingContainer>
      </div>
    </>
  );
};

export default PricingPage;
